/**
 * ****************************************************************************
 * @description     :   Config route for all app
 * @created at      :   2022/03/24
 * @created by      :   ANS809 – quypn@ans-asia.com
 * @package         :   Best Security
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

/**
 * Import libraries
 */
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';
import messages, { MSG_TYPE, MSG_TITLE } from '../utils/messages';
import _ from 'lodash';

/**
 * Import other part of router
 */
import BsRoutes from '@/router/bs-router';
import BmRoutes from '@/router/bm-router';
import BcRoutes from '@/router/bc-router';
import BbRoutes from '@/router/bb-router';
import BfRoutes from '@/router/bf-router';
import BeRoutes from '@/router/be-router';
import BaRoutes from '@/router/ba-router';
import BgRoutes from '@/router/bg-router';
import BhRoutes from '@/router/bh-router';
import BxRoutes from '@/router/bx-router';

/**
 * Import component of view
 */
const PageNotFound = () => import('@/views/common/404');
const ServerError = () => import('@/views/common/500');
const StandardForm = () => import('@/views/common/standardform');
const bs002000i = () => import('@/views/bs/bs002000i');

/**
 * Defined some base router
 */
const baseRoutes = [
    {
        path: '/404',
        name: 'PageNotFound',
        component: PageNotFound,
        meta: {
            title: 'エラー 404'
        }
    },
    {
        path: '/500',
        name: 'ServerError',
        component: ServerError,
        meta: {
            title: 'エラー 500'
        }
    },
    {
        path: '/',
        name: 'Dashboard',
        component: bs002000i,
        meta: {
            title: '基幹システム'
        }
    },
    {
        path: '/standard-form',
        name: 'StandardForm',
        component: StandardForm,
        meta: {
            title: '基幹システム'
        }
    },
    {
        path: '/:catchAll(.*)', // Unrecognized path automatically matches 404
        redirect: '/404'
    }
];

/**
 * Concat all parts of router
 */
const routes = baseRoutes
    .concat(BsRoutes)
    .concat(BmRoutes)
    .concat(BcRoutes)
    .concat(BbRoutes)
    .concat(BfRoutes)
    .concat(BeRoutes)
    .concat(BaRoutes)
    .concat(BgRoutes)
    .concat(BhRoutes)
    .concat(BxRoutes);

/**
 * Defined Vue Router
 */
const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior() {
        return { top: 0 };
    }
});

/**
 * Change title of website on browser when change page
 * -----------------------------------------
 * @author : QuyPN - 2022/03/24 - create
 * @params : to - object - Data of router will redirect to
 * @params : from - object - Data of current router
 * @params : next - function - Continue redirect
 * @access : public
 */
router.beforeEach((to, from, next) => {
    const func_backing = () => {
        let historyURL = JSON.parse(sessionStorage.getItem('historyURL'));
        if (!historyURL) {
            historyURL = [];
        }
        let lastObj = _.last(historyURL);
        if (store._state.data.app.isBacking) {
            // remove localstorge
            store.commit('app/setBacking', false);
        } else {
            // append localstorge
            if (from.name) {
                if (lastObj && lastObj.path != from.path) {
                    historyURL.push({
                        name: from.name,
                        path: from.path,
                        p: from.query
                    });
                } else {
                    historyURL.push({
                        name: 'Bs002000i',
                        path: from.path,
                        p: from.query
                    });
                }
            }
            sessionStorage.setItem('historyURL', JSON.stringify(historyURL));
        }
        if (to.path == '/' || to.path == '/bs/bs002000i') {
            sessionStorage.removeItem('historyURL');
        }
    };
    func_backing();
    const func_logout = () => {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('historyURL');
        sessionStorage.removeItem('bs002000i');
        sessionStorage.removeItem('tableXY');
        store.commit('app/resetAppState', false);
        store.commit('app/setLogout', false);
        store.commit('app/setLogin', false);
    };
    const func_beforeEach = (to, from, next) => {
        store.commit('app/hideMenu');
        store.commit('app/hideHeaderError');
        const publicPages = ['/bs/bs001000i'];
        const authRequired = !publicPages.includes(to.path);
        let loggedIn = false;
        const token = sessionStorage.getItem('token');
        const tokenTimeout = sessionStorage.getItem('tokenTimeout');
        if (token && tokenTimeout && new Date(tokenTimeout) >= new Date()) {
            loggedIn = true;
            store.commit('app/setLogin', true);
            if (!authRequired) {
                return next('/');
            }
        } else {
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('tokenTimeout');
        }
        if (authRequired && !loggedIn) {
            localStorage.setItem('beforeUrl', window.location.pathname);
            if (!store._state.data.app.isLogin) {
                store.commit('app/setLogin', false);
                return next('/bs/bs001000i');
            } else {
                store.commit('app/showModalMessage', {
                    type: MSG_TYPE.ERROR,
                    title: MSG_TITLE.E999,
                    content: messages.E401,
                    callback: () => {
                        store.commit('app/setLogin', false);
                        return next('/bs/bs001000i');
                    }
                });
            }
        }
        if (!authRequired && loggedIn) {
            store.commit('app/showModalMessage', {
                type: MSG_TYPE.ERROR,
                title: MSG_TITLE.E999,
                content: messages.E401,
                callback: () => {
                    store.commit('app/setLogin', false);
                    return next('/');
                }
            });
        }
        if (to.path == '/') {
            const url = localStorage.getItem('urlAfterLogin');
            if (url) {
                return next(url);
            }
        }
        document.title =
            (to.meta.title || 'ベストセキュリティ') + ' | ベストセキュリティ';
        next();
    };
    if (store._state.data.app.isDataScreenChange) {
        store.commit('app/showModalMessage', {
            type: MSG_TYPE.CONFIRM,
            content: messages.C044,
            okText: 'このページを離れる',
            cancelText: 'キャンセル',
            callback: (ok) => {
                if (ok) {
                    if (store._state.data.app.isLogout) {
                        func_logout();
                    }
                    func_beforeEach(to, from, next);
                    store.commit('app/setDataScreenChange', false);
                } else {
                    store.commit('app/setLogout', false);
                    next(false);
                }
            }
        });
    } else {
        if (store._state.data.app.isLogout) {
            func_logout();
        }
        func_beforeEach(to, from, next);
    }
});

export default router;
